import React from 'react';

import type { MessageDescriptor } from 'react-intl';

import type { QuickInsertItem } from '@atlaskit/editor-common/provider-factory/quick-insert-provider';
import { createQuickInsertMatcher } from '@atlaskit/editor-common/quick-insert/create-quick-insert-matcher';
import {
	BLOCK_TEMPLATES_SECTION,
	getBlockTemplateMenuItem,
} from '@atlaskit/editor-common/quick-insert/keys';
import { getSnippetMenuRank } from '@atlaskit/editor-common/quick-insert/snippet-menu-rank';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { RegisterMenuItem } from '@atlaskit/editor-ui-control-model/types';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import type { Snippet } from '@atlassian/editor-snippets-provider/types';

import type { SnippetsPlugin } from '../../snippetsPluginType';
import { SnippetQuickInsertMenuItem } from './SnippetQuickInsertMenuItem';

type Params = {
	api: ExtractInjectionAPI<SnippetsPlugin> | undefined;
	getItem: (
		snippet: Snippet,
		api: ExtractInjectionAPI<SnippetsPlugin> | undefined,
		intl: {
			formatMessage: (
				descriptor: MessageDescriptor,
				values?: Record<string, string | number | boolean | Date>,
			) => string;
		},
	) => QuickInsertItem;
	snippets: Snippet[];
};

export const getSnippetQuickInsertComponents = ({
	api,
	getItem,
	snippets,
}: Params): RegisterMenuItem[] =>
	snippets.map((snippet) => {
		const menuItem = getBlockTemplateMenuItem(snippet.snippetId);
		const isSnippetRemoved = () =>
			isExperimentEnabled('platform_editor_snippets_plugin_confluence') &&
			api?.snippets?.sharedState.currentState()?.removedSnippetIds.has(snippet.snippetId) === true;
		const snippetMatcher = createQuickInsertMatcher(({ formatMessage }) => {
			const item = getItem(snippet, api, { formatMessage });
			return {
				description: item.description,
				keywords: item.keywords,
				shortcut: item.keyshortcut,
				title: item.title,
			};
		});
		const match = Object.assign(
			(context: Parameters<typeof snippetMatcher>[0]) =>
				isSnippetRemoved() ? null : snippetMatcher(context),
			snippetMatcher,
		);
		return {
			key: menuItem.key,
			type: menuItem.type,
			parents: [
				{
					key: BLOCK_TEMPLATES_SECTION.key,
					type: BLOCK_TEMPLATES_SECTION.type,
					rank: getSnippetMenuRank({
						insertCount: snippet.insertCount,
						isFavorite: snippet.isFavorite,
						scope: snippet.scope,
						updatedAt: Number(snippet.updatedAt),
					}),
				},
			],
			isHidden: isSnippetRemoved,
			match,
			component: () => <SnippetQuickInsertMenuItem api={api} getItem={getItem} snippet={snippet} />,
		};
	});
